import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Radar } from 'react-chartjs-2';
import { useBrand } from '../contexts/BrandContext';
import { auditsApi } from '../services/audits';
import { tasksApi } from '../services/tasks';
import type { Audit, Task } from '@traaker/shared';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  RadialLinearScale,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

type DateRange = '7d' | '30d' | '90d' | '180d' | 'all';

const RANGE_DAYS: Record<DateRange, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
  all: null,
};

const COLORS = {
  geo: '#7C3AED',          // theme primary — hero score
  website: '#0B0B0F',      // ink — neutral hero accent
  brandPerception: '#7C3AED',
  earnedMedia: '#EA580C',  // warm orange (status accent)
  freshness: '#10B981',
  seo: '#0EA5E9',
  quality: '#EA580C',
  brandAlignment: '#7C3AED',
  narrative: '#7C3AED',
  authority: '#0E9F6E',
  referral: '#EA580C',
  eeat: '#0EA5E9',
  wikipedia: '#10B981',
  accuracy: '#EA580C',
  mentions: '#7C3AED',
  up: '#10B981',
  down: '#EF4444',
};

function signalColor(v: number): string {
  if (v < 30) return '#EF4444';
  if (v < 60) return '#F59E0B';
  return '#10B981';
}

const GEO_WEIGHTS = {
  brandPerception: 0.4,
  website: 0.3,
  earnedMedia: 0.3,
};

// The brand-perception "roll-up" used by the GEO score blends the three axes
// equally. Each axis is scored independently by its own agent.
const BRAND_AXIS_WEIGHTS = { narrative: 1 / 3, authority: 1 / 3, referral: 1 / 3 };

function computeBrandRollup(narrative: number | null, authority: number | null, referral: number | null): number | null {
  const parts: { value: number; weight: number }[] = [];
  if (narrative !== null) parts.push({ value: narrative, weight: BRAND_AXIS_WEIGHTS.narrative });
  if (authority !== null) parts.push({ value: authority, weight: BRAND_AXIS_WEIGHTS.authority });
  if (referral !== null) parts.push({ value: referral, weight: BRAND_AXIS_WEIGHTS.referral });
  if (parts.length === 0) return null;
  const totalWeight = parts.reduce((s, p) => s + p.weight, 0);
  return parts.reduce((s, p) => s + p.value * p.weight, 0) / totalWeight;
}

function computeGeoScore(website: number | null, brand: number | null, earned: number | null): number | null {
  const parts: { value: number; weight: number }[] = [];
  if (website !== null) parts.push({ value: website, weight: GEO_WEIGHTS.website });
  if (brand !== null) parts.push({ value: brand, weight: GEO_WEIGHTS.brandPerception });
  if (earned !== null) parts.push({ value: earned, weight: GEO_WEIGHTS.earnedMedia });
  if (parts.length === 0) return null;
  const totalWeight = parts.reduce((sum, p) => sum + p.weight, 0);
  return parts.reduce((sum, p) => sum + p.value * p.weight, 0) / totalWeight;
}

const GRID_COLOR = 'rgba(11, 11, 15, 0.06)';
const TICK_COLOR = 'rgba(11, 11, 15, 0.55)';
const LEGEND_COLOR = '#0B0B0F';

const CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' as const },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: { usePointStyle: true, padding: 16, color: LEGEND_COLOR, font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#0B0B0F',
      bodyColor: '#0B0B0F',
      borderColor: '#E6E7EB',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 12,
      titleFont: { weight: 600 as const },
    },
  },
  scales: {
    y: {
      min: 0,
      max: 100,
      grid: { color: GRID_COLOR },
      ticks: { color: TICK_COLOR, maxTicksLimit: 6 },
    },
    x: {
      grid: { display: false },
      ticks: { color: TICK_COLOR, maxTicksLimit: 8, autoSkip: true, maxRotation: 0 },
    },
  },
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function filterByRange(audits: Audit[], range: DateRange): Audit[] {
  const days = RANGE_DAYS[range];
  if (!days) return audits;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return audits.filter((a) => new Date(a.createdAt) >= cutoff);
}

function filterTasksByRange(tasks: Task[], range: DateRange): Task[] {
  const days = RANGE_DAYS[range];
  if (!days) return tasks;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return tasks.filter((t) => new Date(t.updatedAt) >= cutoff);
}

function calcTrend(values: number[]): { delta: number; direction: 'up' | 'down' | 'flat' } {
  if (values.length < 2) return { delta: 0, direction: 'flat' };
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const delta = Math.round(last - prev);
  return { delta, direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' };
}

export default function Dashboard() {
  const { activeBrand } = useBrand();
  const [range, setRange] = useState<DateRange>('30d');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [websiteAudits, setWebsiteAudits] = useState<Audit[]>([]);
  const [narrativeAudits, setNarrativeAudits] = useState<Audit[]>([]);
  const [authorityAudits, setAuthorityAudits] = useState<Audit[]>([]);
  const [referralAudits, setReferralAudits] = useState<Audit[]>([]);
  const [earnedAudits, setEarnedAudits] = useState<Audit[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);

  const loadData = useCallback(async () => {
    if (!activeBrand) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [website, narrative, authority, referral, earned, allTasks] = await Promise.all([
        auditsApi.list('website', activeBrand.brandId),
        auditsApi.list('brand_narrative', activeBrand.brandId),
        auditsApi.list('brand_authority', activeBrand.brandId),
        auditsApi.list('brand_referral', activeBrand.brandId),
        auditsApi.list('earned_media', activeBrand.brandId),
        tasksApi.list(activeBrand.brandId),
      ]);
      const doneSorted = (arr: Audit[]) =>
        arr.filter((a) => a.status === 'completed').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      setWebsiteAudits(doneSorted(website));
      setNarrativeAudits(doneSorted(narrative));
      setAuthorityAudits(doneSorted(authority));
      setReferralAudits(doneSorted(referral));
      setEarnedAudits(doneSorted(earned));
      setTasks(allTasks.sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)));
    } catch {
      // silently handled
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const completedTasks = useMemo(() => tasks.filter((t) => t.status === 'done'), [tasks]);

  const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const openTasks = useMemo(
    () => tasks
      .filter((t) => t.status === 'todo' || t.status === 'in_progress')
      .sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority] ?? 9;
        const pb = PRIORITY_ORDER[b.priority] ?? 9;
        if (pa !== pb) return pa - pb;
        return b.updatedAt.localeCompare(a.updatedAt);
      }),
    [tasks],
  );

  const filtered = useMemo(() => ({
    website: filterByRange(websiteAudits, range),
    narrative: filterByRange(narrativeAudits, range),
    authority: filterByRange(authorityAudits, range),
    referral: filterByRange(referralAudits, range),
    earned: filterByRange(earnedAudits, range),
    doneTasks: filterTasksByRange(completedTasks, range),
  }), [websiteAudits, narrativeAudits, authorityAudits, referralAudits, earnedAudits, completedTasks, range]);

  const brandAxisCount = filtered.narrative.length + filtered.authority.length + filtered.referral.length;
  const totalAudits = filtered.website.length + brandAxisCount + filtered.earned.length;

  const lastAuditDate = useMemo(() => {
    const all = [
      ...websiteAudits,
      ...narrativeAudits,
      ...authorityAudits,
      ...referralAudits,
      ...earnedAudits,
    ].map((a) => a.createdAt).sort();
    return all.length ? all[all.length - 1] : null;
  }, [websiteAudits, narrativeAudits, authorityAudits, referralAudits, earnedAudits]);

  const lastAuditLabel = useMemo(() => {
    if (!lastAuditDate) return null;
    const diffDays = Math.floor((Date.now() - new Date(lastAuditDate).getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "aujourd'hui";
    if (diffDays === 1) return 'hier';
    if (diffDays < 30) return `il y a ${diffDays} jours`;
    if (diffDays < 60) return 'il y a 1 mois';
    return `il y a ${Math.floor(diffDays / 30)} mois`;
  }, [lastAuditDate]);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            Vue d'ensemble
          </Typography>
          {activeBrand && (
            <Typography variant="body2" color="text.secondary">
              Performance GEO de <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{activeBrand.name}</Box>
              {lastAuditLabel && <> · Dernier audit {lastAuditLabel}</>}
              {totalAudits > 0 && <> · {totalAudits} audit{totalAudits > 1 ? 's' : ''} sur la période</>}
            </Typography>
          )}
        </Box>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, v) => v && setRange(v)}
          size="small"
        >
          <ToggleButton value="7d">7j</ToggleButton>
          <ToggleButton value="30d">30j</ToggleButton>
          <ToggleButton value="90d">90j</ToggleButton>
          <ToggleButton value="180d">6m</ToggleButton>
          <ToggleButton value="all">Tout</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {!activeBrand ? (
        <Alert severity="info">Sélectionnez une marque pour voir vos analytics.</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : totalAudits === 0 ? (
        <Alert severity="info">
          Aucun audit terminé pour <strong>{activeBrand.name}</strong> sur cette période.
          Lancez des audits pour voir apparaître vos données ici.
        </Alert>
      ) : (
        <>
          <KpiRow
            website={filtered.website}
            narrative={filtered.narrative}
            authority={filtered.authority}
            referral={filtered.referral}
            earned={filtered.earned}
            doneTasks={filtered.doneTasks}
          />

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 3, mb: 2 }} variant="scrollable" scrollButtons="auto">
            <Tab label="Vue d'ensemble" />
            <Tab label="Website" disabled={filtered.website.length === 0} />
            <Tab label="Brand Narrative" disabled={filtered.narrative.length === 0} />
            <Tab label="Brand Authority" disabled={filtered.authority.length === 0} />
            <Tab label="Brand Referral" disabled={filtered.referral.length === 0} />
            <Tab label="Earned Media" disabled={filtered.earned.length === 0} />
            <Tab label="Moteurs IA" disabled={brandAxisCount === 0} />
            <Tab label="Zones" disabled={filtered.referral.length === 0} />
          </Tabs>

          {tab === 0 && (
            <OverviewTab
              website={filtered.website}
              narrative={filtered.narrative}
              authority={filtered.authority}
              referral={filtered.referral}
              earned={filtered.earned}
              openTasks={openTasks}
              range={range}
            />
          )}
          {tab === 1 && <WebsiteTab audits={filtered.website} />}
          {tab === 2 && <NarrativeTab audits={filtered.narrative} />}
          {tab === 3 && <AuthorityTab audits={filtered.authority} />}
          {tab === 4 && <ReferralTab audits={filtered.referral} />}
          {tab === 5 && <EarnedMediaTab audits={filtered.earned} />}
          {tab === 6 && (
            <EnginesTab
              narrative={filtered.narrative}
              authority={filtered.authority}
              referral={filtered.referral}
            />
          )}
          {tab === 7 && <ZonesTab audits={filtered.referral} />}
        </>
      )}
    </Box>
  );
}

/* ──────────────── KPI Row ──────────────── */

function latestScore<T extends Audit>(audits: T[], pick: (a: T) => number | null | undefined): number | null {
  for (let i = audits.length - 1; i >= 0; i -= 1) {
    const v = pick(audits[i]);
    if (typeof v === 'number') return v;
  }
  return null;
}

function KpiRow({ website, narrative, authority, referral, earned, doneTasks }: {
  website: Audit[];
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
  earned: Audit[];
  doneTasks: Task[];
}) {
  const wsScores = website.map((a) => a.websiteSummary?.overallGeoScore ?? 0);
  const narScores = narrative.map((a) => a.brandNarrativeSummary?.score ?? 0);
  const authScores = authority.map((a) => a.brandAuthoritySummary?.score ?? 0);
  const refScores = referral.map((a) => a.brandReferralSummary?.score ?? 0);
  const emScores = earned.map((a) => a.earnedMediaSummary?.overall_earned_media_score ?? 0);

  const latestWs = latestScore(website, (a) => a.websiteSummary?.overallGeoScore);
  const latestNar = latestScore(narrative, (a) => a.brandNarrativeSummary?.score);
  const latestAuth = latestScore(authority, (a) => a.brandAuthoritySummary?.score);
  const latestRef = latestScore(referral, (a) => a.brandReferralSummary?.score);
  const latestEm = latestScore(earned, (a) => a.earnedMediaSummary?.overall_earned_media_score);

  const wsTrend = calcTrend(wsScores);
  const narTrend = calcTrend(narScores);
  const authTrend = calcTrend(authScores);
  const refTrend = calcTrend(refScores);
  const emTrend = calcTrend(emScores);

  const latestBrandRollup = computeBrandRollup(latestNar, latestAuth, latestRef);
  const latestGeo = computeGeoScore(latestWs, latestBrandRollup, latestEm);

  // Build a GEO series using latest-known values per audit date to compute a trend.
  const allDates = [
    ...website.map((a) => ({ date: a.createdAt, type: 'w' as const, audit: a })),
    ...narrative.map((a) => ({ date: a.createdAt, type: 'n' as const, audit: a })),
    ...authority.map((a) => ({ date: a.createdAt, type: 'a' as const, audit: a })),
    ...referral.map((a) => ({ date: a.createdAt, type: 'r' as const, audit: a })),
    ...earned.map((a) => ({ date: a.createdAt, type: 'e' as const, audit: a })),
  ].sort((a, b) => a.date.localeCompare(b.date));
  let lw: number | null = null, ln: number | null = null, la: number | null = null, lr: number | null = null, le: number | null = null;
  const geoSeries: number[] = [];
  for (const entry of allDates) {
    if (entry.type === 'w') lw = entry.audit.websiteSummary?.overallGeoScore ?? lw;
    if (entry.type === 'n') ln = entry.audit.brandNarrativeSummary?.score ?? ln;
    if (entry.type === 'a') la = entry.audit.brandAuthoritySummary?.score ?? la;
    if (entry.type === 'r') lr = entry.audit.brandReferralSummary?.score ?? lr;
    if (entry.type === 'e') le = entry.audit.earnedMediaSummary?.overall_earned_media_score ?? le;
    const brandRoll = computeBrandRollup(ln, la, lr);
    const g = computeGeoScore(lw, brandRoll, le);
    if (g !== null) geoSeries.push(g);
  }
  const geoTrend = calcTrend(geoSeries);

  const personaCounts = {
    technique: doneTasks.filter((t) => t.personaTab === 'technique').length,
    contenu: doneTasks.filter((t) => t.personaTab === 'contenu').length,
    hors_site: doneTasks.filter((t) => t.personaTab === 'hors_site').length,
  };

  const personaParts = [
    personaCounts.technique && `${personaCounts.technique} tech`,
    personaCounts.contenu && `${personaCounts.contenu} contenu`,
    personaCounts.hors_site && `${personaCounts.hors_site} hors-site`,
  ].filter(Boolean).join(' · ');

  return (
    <Grid container spacing={1.5}>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard
          label="Score GEO"
          value={latestGeo}
          trend={geoTrend}
          color={COLORS.geo}
          tooltip={`${Math.round(GEO_WEIGHTS.brandPerception * 100)}% Brand Perception + ${Math.round(GEO_WEIGHTS.website * 100)}% Website + ${Math.round(GEO_WEIGHTS.earnedMedia * 100)}% Earned Media`}
        />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard label="Website" value={latestWs} trend={wsTrend} color={COLORS.website} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard label="Narrative" value={latestNar} trend={narTrend} color={COLORS.narrative} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard label="Authority" value={latestAuth} trend={authTrend} color={COLORS.authority} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard label="Referral" value={latestRef} trend={refTrend} color={COLORS.referral} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <KpiCard label="Earned Media" value={latestEm} trend={emTrend} color={COLORS.earnedMedia} />
      </Grid>
      <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }} title={personaParts || undefined}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                display: 'block',
              }}
            >
              Tâches faites
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
              <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {doneTasks.length}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, fontSize: '0.6875rem', minHeight: '1em' }}>
              {personaParts || '—'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function KpiCard({ label, value, trend, color, tooltip }: {
  label: string;
  value: number | null;
  trend: { delta: number; direction: 'up' | 'down' | 'flat' };
  color: string;
  tooltip?: string;
}) {
  const trendColor = trend.direction === 'up' ? COLORS.up : trend.direction === 'down' ? COLORS.down : '#5F6470';
  const trendBg = trend.direction === 'up' ? 'rgba(16,185,129,0.10)' : trend.direction === 'down' ? 'rgba(239,68,68,0.10)' : 'rgba(11,11,15,0.06)';
  const pct = value !== null ? Math.max(0, Math.min(100, value)) : 0;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }} title={tooltip}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.02em', lineHeight: 1.1 }}>
            {value !== null ? Math.round(value) : '--'}
          </Typography>
          {trend.delta !== 0 ? (
            <Box
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: trendColor,
                bgcolor: trendBg,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.75,
                lineHeight: 1.4,
              }}
            >
              {trend.delta > 0 ? '+' : ''}{trend.delta}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>—</Typography>
          )}
        </Box>
        <Box sx={{ mt: 1.25, height: 4, borderRadius: 2, bgcolor: 'rgba(11,11,15,0.06)', overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, transition: 'width 0.3s ease' }} />
        </Box>
      </CardContent>
    </Card>
  );
}

/* ──────────────── Overview Tab ──────────────── */

const RANGE_LABEL: Record<DateRange, string> = {
  '7d': '7 derniers jours',
  '30d': '30 derniers jours',
  '90d': '90 derniers jours',
  '180d': '180 derniers jours',
  all: 'Tout l\'historique',
};

function OverviewTab({ website, narrative, authority, referral, earned, openTasks, range }: {
  website: Audit[];
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
  earned: Audit[];
  openTasks: Task[];
  range: DateRange;
}) {
  // Resample the GEO score onto an evenly-spaced timeline so the line shape is
  // independent of audit cadence. We aggregate raw audit events into a single
  // chronological stream, replay them through a carry-forward state, and snap
  // each bucket to the most recent computed GEO known at the bucket boundary.
  type AuditEvent =
    | { date: number; type: 'website'; value: number | null }
    | { date: number; type: 'narrative'; value: number | null }
    | { date: number; type: 'authority'; value: number | null }
    | { date: number; type: 'referral'; value: number | null }
    | { date: number; type: 'earned_media'; value: number | null };

  const events: AuditEvent[] = [
    ...website.map<AuditEvent>((a) => ({ date: new Date(a.createdAt).getTime(), type: 'website', value: a.websiteSummary?.overallGeoScore ?? null })),
    ...narrative.map<AuditEvent>((a) => ({ date: new Date(a.createdAt).getTime(), type: 'narrative', value: a.brandNarrativeSummary?.score ?? null })),
    ...authority.map<AuditEvent>((a) => ({ date: new Date(a.createdAt).getTime(), type: 'authority', value: a.brandAuthoritySummary?.score ?? null })),
    ...referral.map<AuditEvent>((a) => ({ date: new Date(a.createdAt).getTime(), type: 'referral', value: a.brandReferralSummary?.score ?? null })),
    ...earned.map<AuditEvent>((a) => ({ date: new Date(a.createdAt).getTime(), type: 'earned_media', value: a.earnedMediaSummary?.overall_earned_media_score ?? null })),
  ].sort((a, b) => a.date - b.date);

  const labels: Date[] = [];
  const geoLine: (number | null)[] = [];

  if (events.length > 0) {
    const firstAuditTs = events[0].date;
    const days = RANGE_DAYS[range];
    const now = Date.now();
    const windowStart = days ? Math.max(now - days * 86400000, firstAuditTs) : firstAuditTs;
    const windowEnd = now;
    const windowMs = windowEnd - windowStart;

    // Pick a bucket size that yields ~80–120 points across the window so the
    // line is smooth at any range. Sub-day buckets for the "live" 7-day view.
    const targetBuckets = 100;
    const rawBucket = Math.max(windowMs / targetBuckets, 60_000);
    const HOUR = 3_600_000;
    const DAY = 86_400_000;
    const bucketMs =
      rawBucket <= HOUR ? HOUR :
      rawBucket <= 6 * HOUR ? 6 * HOUR :
      rawBucket <= DAY ? DAY :
      rawBucket <= 7 * DAY ? Math.ceil(rawBucket / DAY) * DAY :
      Math.ceil(rawBucket / DAY) * DAY;

    // Replay events that happened BEFORE windowStart to seed carry-forward state.
    let lastWebsite: number | null = null;
    let lastNar: number | null = null;
    let lastAuth: number | null = null;
    let lastRef: number | null = null;
    let lastEm: number | null = null;
    let cursor = 0;
    while (cursor < events.length && events[cursor].date < windowStart) {
      const e = events[cursor];
      if (e.value !== null) {
        if (e.type === 'website') lastWebsite = e.value;
        if (e.type === 'narrative') lastNar = e.value;
        if (e.type === 'authority') lastAuth = e.value;
        if (e.type === 'referral') lastRef = e.value;
        if (e.type === 'earned_media') lastEm = e.value;
      }
      cursor += 1;
    }

    for (let bucketEnd = windowStart + bucketMs; bucketEnd <= windowEnd + bucketMs; bucketEnd += bucketMs) {
      while (cursor < events.length && events[cursor].date < bucketEnd) {
        const e = events[cursor];
        if (e.value !== null) {
          if (e.type === 'website') lastWebsite = e.value;
          if (e.type === 'narrative') lastNar = e.value;
          if (e.type === 'authority') lastAuth = e.value;
          if (e.type === 'referral') lastRef = e.value;
          if (e.type === 'earned_media') lastEm = e.value;
        }
        cursor += 1;
      }
      const brandRoll = computeBrandRollup(lastNar, lastAuth, lastRef);
      const geo = computeGeoScore(lastWebsite, brandRoll, lastEm);
      labels.push(new Date(Math.min(bucketEnd, windowEnd)));
      geoLine.push(geo);
    }
  }

  // Tick formatter: hour labels for sub-day buckets, day/month otherwise.
  const subDayBuckets = labels.length > 1 && (labels[1].getTime() - labels[0].getTime()) < 86_400_000;
  const tickFormatter = (_raw: string | number, idx: number): string => {
    const d = labels[idx];
    if (!d) return '';
    if (subDayBuckets) {
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const geoLineData = {
    labels: labels.map((d) => d.toISOString()),
    datasets: [
      {
        label: 'Score GEO',
        data: geoLine,
        borderColor: COLORS.geo,
        backgroundColor: (ctx: { chart: { ctx: CanvasRenderingContext2D; chartArea?: { top: number; bottom: number } } }) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return 'rgba(124,58,237,0.18)';
          const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(124,58,237,0.28)');
          gradient.addColorStop(1, 'rgba(124,58,237,0)');
          return gradient;
        },
        fill: true,
        tension: 0.35,
        spanGaps: true,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointBackgroundColor: COLORS.geo,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
      },
    ],
  };

  const overviewOptions = {
    ...CHART_DEFAULTS,
    plugins: {
      ...CHART_DEFAULTS.plugins,
      legend: { display: false },
      tooltip: {
        ...CHART_DEFAULTS.plugins.tooltip,
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const d = items[0] && labels[items[0].dataIndex];
            if (!d) return '';
            return subDayBuckets
              ? d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
              : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
          },
          label: (item: { parsed: { y: number | null } }) => {
            const v = item.parsed.y;
            return v === null ? 'Score GEO : —' : `Score GEO : ${Math.round(v)}/100`;
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: { color: GRID_COLOR, drawTicks: false },
        ticks: { color: TICK_COLOR, maxTicksLimit: 4, padding: 8 },
        border: { display: false },
      },
      x: {
        grid: { display: false },
        ticks: {
          color: TICK_COLOR,
          maxTicksLimit: 8,
          autoSkip: true,
          maxRotation: 0,
          callback: tickFormatter,
        },
        border: { display: false },
      },
    },
  };

  const latestWebsite = website.length ? website[website.length - 1].websiteSummary : null;
  const latestNarrative = narrative.length ? narrative[narrative.length - 1].brandNarrativeSummary : null;
  const latestAuthority = authority.length ? authority[authority.length - 1].brandAuthoritySummary : null;
  const latestReferral = referral.length ? referral[referral.length - 1].brandReferralSummary : null;
  const latestEarned = earned.length ? earned[earned.length - 1].earnedMediaSummary : null;

  const latestNarScore = latestNarrative?.score ?? null;
  const latestAuthScore = latestAuthority?.score ?? null;
  const latestRefScore = latestReferral?.score ?? null;
  const latestGeo = computeGeoScore(
    latestWebsite?.overallGeoScore ?? null,
    computeBrandRollup(latestNarScore, latestAuthScore, latestRefScore),
    latestEarned?.overall_earned_media_score ?? null,
  );

  const radarData = {
    labels: ['Website', 'Narrative', 'Authority', 'Referral', 'Earned Media', 'E-E-A-T'],
    datasets: [{
      label: 'Dernier audit',
      data: [
        latestWebsite?.overallGeoScore ?? 0,
        latestNarrative?.score ?? 0,
        latestAuthority?.score ?? 0,
        latestReferral?.score ?? 0,
        latestEarned?.overall_earned_media_score ?? 0,
        latestEarned?.eeat_score ?? 0,
      ],
      borderColor: COLORS.geo,
      backgroundColor: `${COLORS.geo}20`,
      pointBackgroundColor: COLORS.geo,
    }],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0, max: 100,
        ticks: { stepSize: 20, display: false },
        grid: { color: GRID_COLOR },
        angleLines: { color: GRID_COLOR },
        pointLabels: { font: { size: 11 }, color: LEGEND_COLOR },
      },
    },
    plugins: { legend: { display: false } },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Score GEO — évolution
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {RANGE_LABEL[range]}
              </Typography>
            </Box>
            <GeoScoreHero
              geo={latestGeo}
              narrative={latestNarScore}
              authority={latestAuthScore}
              referral={latestRefScore}
            />
            <Box sx={{ height: 220, mt: 2 }}>
              <Line data={geoLineData} options={overviewOptions} />
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <OpenTasksCard tasks={openTasks} />
          <ChartCard title="Radar global">
            <Box sx={{ height: 280 }}>
              <Radar data={radarData} options={radarOptions} />
            </Box>
          </ChartCard>
        </Stack>
      </Grid>
    </Grid>
  );
}

/* ──────────────── GEO Hero (ring + axis chips) ──────────────── */

function GeoScoreHero({ geo, narrative, authority, referral }: {
  geo: number | null;
  narrative: number | null;
  authority: number | null;
  referral: number | null;
}) {
  const ringValue = geo !== null ? Math.max(0, Math.min(100, Math.round(geo))) : 0;
  const ringDeg = (ringValue / 100) * 360;
  const ringColor = geo !== null ? COLORS.geo : '#E6E7EB';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <Box
        sx={{
          position: 'relative',
          width: 110,
          height: 110,
          borderRadius: '50%',
          flexShrink: 0,
          background: `conic-gradient(${ringColor} 0deg ${ringDeg}deg, #EEEEF1 ${ringDeg}deg 360deg)`,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 10,
            borderRadius: '50%',
            bgcolor: 'background.paper',
          }}
        />
        <Box sx={{ position: 'relative', textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1, letterSpacing: '-0.02em' }}>
            {geo !== null ? Math.round(geo) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            / 100
          </Typography>
        </Box>
      </Box>

      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 200 }}>
        <AxisRow label="Narrative 1/3" value={narrative} bg="rgba(124,58,237,0.10)" color="#7C3AED" border="rgba(124,58,237,0.20)" />
        <AxisRow label="Authority 1/3" value={authority} bg="rgba(14,159,110,0.10)"  color="#0E9F6E" border="rgba(14,159,110,0.20)" />
        <AxisRow label="Referral 1/3"  value={referral}  bg="rgba(234,88,12,0.10)"   color="#EA580C" border="rgba(234,88,12,0.22)" />
        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
          Pondéré par axe (1/3 chacun)
        </Typography>
      </Stack>
    </Box>
  );
}

function AxisRow({ label, value, bg, color, border }: {
  label: string;
  value: number | null;
  bg: string;
  color: string;
  border: string;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color,
          bgcolor: bg,
          border: '1px solid',
          borderColor: border,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          lineHeight: 1.5,
        }}
      >
        {label}
      </Box>
      <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
        {value !== null ? Math.round(value) : '—'}
      </Typography>
    </Box>
  );
}

/* ──────────────── Open Tasks Card ──────────────── */

const PERSONA_META: Record<string, { label: string; color: string; bg: string }> = {
  technique: { label: 'Technique', color: '#7C3AED', bg: 'rgba(124,58,237,0.10)' },
  contenu:   { label: 'Contenu',   color: '#0E9F6E', bg: 'rgba(14,159,110,0.10)' },
  hors_site: { label: 'Hors-site', color: '#EA580C', bg: 'rgba(234,88,12,0.10)' },
};

const PRIORITY_META: Record<string, { label: string; color: string }> = {
  critical: { label: 'Critique', color: '#EF4444' },
  high:     { label: 'Haute',    color: '#F97316' },
  medium:   { label: 'Moyenne',  color: '#F59E0B' },
  low:      { label: 'Basse',    color: '#5F6470' },
};

function OpenTasksCard({ tasks }: { tasks: Task[] }) {
  const top = tasks.slice(0, 4);

  return (
    <Card>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Tâches à attaquer
          </Typography>
          <Box
            component={RouterLink}
            to="/tasks"
            sx={{
              fontSize: 12,
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 600,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Voir tout →
          </Box>
        </Box>
        {top.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Aucune tâche ouverte. Lancez un audit pour générer des recommandations.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {top.map((t) => (
              <TaskRow key={t.taskId} task={t} />
            ))}
            {tasks.length > top.length && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
                +{tasks.length - top.length} autre{tasks.length - top.length > 1 ? 's' : ''}
              </Typography>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function TaskRow({ task }: { task: Task }) {
  const persona = PERSONA_META[task.personaTab] || { label: task.personaTab, color: '#5F6470', bg: 'rgba(11,11,15,0.06)' };
  const priority = PRIORITY_META[task.priority] || { label: task.priority, color: '#5F6470' };

  return (
    <Box
      component={RouterLink}
      to="/tasks"
      sx={{
        display: 'block',
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(124,58,237,0.04)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 0.5 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            fontSize: 13,
            lineHeight: 1.35,
          }}
        >
          {task.title}
        </Typography>
        <Box
          sx={{
            flexShrink: 0,
            fontSize: 10.5,
            fontWeight: 600,
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            color: persona.color,
            bgcolor: persona.bg,
            lineHeight: 1.4,
          }}
        >
          {persona.label}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: priority.color }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {priority.label}
          {task.status === 'in_progress' && ' · En cours'}
        </Typography>
      </Box>
    </Box>
  );
}

/* ──────────────── Website Tab ──────────────── */

function WebsiteTab({ audits }: { audits: Audit[] }) {
  const labels = audits.map((a) => formatDate(a.createdAt));

  const lineData = {
    labels,
    datasets: [
      { label: 'Score Website', data: audits.map((a) => a.websiteSummary?.overallGeoScore ?? null), borderColor: COLORS.website, tension: 0.3, spanGaps: true },
      { label: 'Freshness', data: audits.map((a) => a.websiteSummary?.content_freshness?.avg_freshness_score ?? null), borderColor: COLORS.freshness, tension: 0.3, spanGaps: true },
      { label: 'SEO', data: audits.map((a) => a.websiteSummary?.structural_seo?.avg_structural_score ?? null), borderColor: COLORS.seo, borderDash: [5, 5], tension: 0.3, spanGaps: true },
      { label: 'Content Quality', data: audits.map((a) => a.websiteSummary?.content_quality?.avg_geo_readiness ?? null), borderColor: COLORS.quality, tension: 0.3, spanGaps: true },
      { label: 'Brand Alignment', data: audits.map((a) => a.websiteSummary?.content_quality?.avg_brand_alignment ?? null), borderColor: COLORS.brandAlignment, tension: 0.3, spanGaps: true },
    ],
  };

  const latest = audits[audits.length - 1]?.websiteSummary?.content_quality?.signal_averages;
  const signalLabels = latest ? Object.keys(latest) : [];
  const signalData = latest ? {
    labels: signalLabels.map((s) => s.replace(/_/g, ' ')),
    datasets: [{
      label: 'Score moyen',
      data: signalLabels.map((k) => latest[k]),
      backgroundColor: signalLabels.map((k) => signalColor(latest[k] ?? 0)),
      borderRadius: 6,
    }],
  } : null;

  const barOptions = {
    ...CHART_DEFAULTS,
    plugins: { ...CHART_DEFAULTS.plugins, legend: { display: false } },
    scales: {
      ...CHART_DEFAULTS.scales,
      x: { ...CHART_DEFAULTS.scales.x, ticks: { font: { size: 10 } } },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: signalData ? 7 : 12 }}>
        <ChartCard title="Scores website dans le temps">
          <Box sx={{ height: 340 }}>
            <Line data={lineData} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      {signalData && (
        <Grid size={{ xs: 12, md: 5 }}>
          <ChartCard title="Signaux GEO (dernier audit)">
            <Box sx={{ height: 340 }}>
              <Bar data={signalData} options={barOptions} />
            </Box>
          </ChartCard>
        </Grid>
      )}
    </Grid>
  );
}

/* ──────────────── Brand Narrative Tab ──────────────── */

function NarrativeTab({ audits }: { audits: Audit[] }) {
  const labels = audits.map((a) => formatDate(a.createdAt));

  const alignmentWeights: Record<string, number> = { strong: 100, partial: 60, weak: 30, misaligned: 0 };
  const alignmentSeries = audits.map((a) => {
    const s = a.brandNarrativeSummary;
    if (!s) return null;
    const w = alignmentWeights[s.alignment];
    return typeof w === 'number' ? w : null;
  });

  const data = {
    labels,
    datasets: [
      { label: 'Score Narrative', data: audits.map((a) => a.brandNarrativeSummary?.score ?? null), borderColor: COLORS.narrative, borderWidth: 3, tension: 0.3, spanGaps: true },
      { label: 'Alignement (pondéré)', data: alignmentSeries, borderColor: COLORS.brandAlignment, borderDash: [5, 5], tension: 0.3, spanGaps: true },
    ],
  };

  const sentimentData = {
    labels,
    datasets: [
      { label: 'Positif', data: audits.map((a) => a.brandNarrativeSummary?.sentiment_breakdown?.positive ?? 0), backgroundColor: '#10B981', borderRadius: 4 },
      { label: 'Neutre', data: audits.map((a) => a.brandNarrativeSummary?.sentiment_breakdown?.neutral ?? 0), backgroundColor: '#A0A4AE', borderRadius: 4 },
      { label: 'Négatif', data: audits.map((a) => a.brandNarrativeSummary?.sentiment_breakdown?.negative ?? 0), backgroundColor: '#EF4444', borderRadius: 4 },
    ],
  };

  const stackedOptions = {
    ...CHART_DEFAULTS,
    scales: {
      y: { stacked: true, grid: { color: 'rgba(11,11,15,0.06)' } },
      x: { stacked: true, grid: { display: false } },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <ChartCard title="Score Narrative & alignement stratégique">
          <Box sx={{ height: 340 }}>
            <Line data={data} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <ChartCard title="Sentiment des réponses IA">
          <Box sx={{ height: 340 }}>
            <Bar data={sentimentData} options={stackedOptions} />
          </Box>
        </ChartCard>
      </Grid>
    </Grid>
  );
}

/* ──────────────── Brand Authority Tab ──────────────── */

function AuthorityTab({ audits }: { audits: Audit[] }) {
  const labels = audits.map((a) => formatDate(a.createdAt));

  const data = {
    labels,
    datasets: [
      { label: 'Score Authority', data: audits.map((a) => a.brandAuthoritySummary?.score ?? null), borderColor: COLORS.authority, borderWidth: 3, tension: 0.3, spanGaps: true },
      { label: 'Citation domaine (%)', data: audits.map((a) => (a.brandAuthoritySummary?.domain_citation_rate ?? 0) * 100), borderColor: COLORS.freshness, tension: 0.3, spanGaps: true },
      { label: 'Mention dans réponse (%)', data: audits.map((a) => (a.brandAuthoritySummary?.response_text_mention_rate ?? 0) * 100), borderColor: COLORS.brandPerception, borderDash: [5, 5], tension: 0.3, spanGaps: true },
    ],
  };

  const latest = audits[audits.length - 1]?.brandAuthoritySummary;
  const cited = latest?.cited_topics ?? [];
  const missing = latest?.missing_topics ?? [];

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: cited.length || missing.length ? 7 : 12 }}>
        <ChartCard title="Autorité thématique dans le temps">
          <Box sx={{ height: 340 }}>
            <Line data={data} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      {(cited.length > 0 || missing.length > 0) && (
        <Grid size={{ xs: 12, md: 5 }}>
          <ChartCard title="Topics (dernier audit)">
            <Box sx={{ height: 340, overflow: 'auto' }}>
              {cited.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                    CITÉ COMME SOURCE
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 2 }}>
                    {cited.map((t, i) => (
                      <Box
                        key={`c${i}`}
                        sx={{
                          bgcolor: `${COLORS.authority}20`,
                          color: COLORS.authority,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              {missing.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
                    GAPS À COMBLER
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {missing.map((t, i) => (
                      <Box
                        key={`m${i}`}
                        sx={{
                          border: `1px solid ${COLORS.geo}40`,
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {t}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </ChartCard>
        </Grid>
      )}
    </Grid>
  );
}

/* ──────────────── Brand Referral Tab ──────────────── */

function ReferralTab({ audits }: { audits: Audit[] }) {
  const labels = audits.map((a) => formatDate(a.createdAt));

  const data = {
    labels,
    datasets: [
      { label: 'Score Referral', data: audits.map((a) => a.brandReferralSummary?.score ?? null), borderColor: COLORS.referral, borderWidth: 3, tension: 0.3, spanGaps: true },
      { label: 'Taux citation (%)', data: audits.map((a) => (a.brandReferralSummary?.overall_citation_rate ?? 0) * 100), borderColor: COLORS.freshness, tension: 0.3, spanGaps: true },
      { label: 'Taux recommandation (%)', data: audits.map((a) => (a.brandReferralSummary?.overall_recommendation_rate ?? 0) * 100), borderColor: COLORS.brandPerception, borderDash: [5, 5], tension: 0.3, spanGaps: true },
    ],
  };

  const sentimentData = {
    labels,
    datasets: [
      { label: 'Positif', data: audits.map((a) => a.brandReferralSummary?.sentiment_breakdown?.positive ?? 0), backgroundColor: '#10B981', borderRadius: 4 },
      { label: 'Neutre', data: audits.map((a) => a.brandReferralSummary?.sentiment_breakdown?.neutral ?? 0), backgroundColor: '#A0A4AE', borderRadius: 4 },
      { label: 'Négatif', data: audits.map((a) => a.brandReferralSummary?.sentiment_breakdown?.negative ?? 0), backgroundColor: '#EF4444', borderRadius: 4 },
    ],
  };

  const stackedOptions = {
    ...CHART_DEFAULTS,
    scales: {
      y: { stacked: true, grid: { color: 'rgba(11,11,15,0.06)' } },
      x: { stacked: true, grid: { display: false } },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <ChartCard title="Recommandation dans le temps">
          <Box sx={{ height: 340 }}>
            <Line data={data} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <ChartCard title="Sentiment des réponses IA">
          <Box sx={{ height: 340 }}>
            <Bar data={sentimentData} options={stackedOptions} />
          </Box>
        </ChartCard>
      </Grid>
    </Grid>
  );
}

/* ──────────────── Earned Media Tab ──────────────── */

function EarnedMediaTab({ audits }: { audits: Audit[] }) {
  const labels = audits.map((a) => formatDate(a.createdAt));

  const data = {
    labels,
    datasets: [
      { label: 'Earned Media', data: audits.map((a) => a.earnedMediaSummary?.overall_earned_media_score ?? null), borderColor: COLORS.earnedMedia, borderWidth: 3, tension: 0.3, spanGaps: true },
      { label: 'E-E-A-T', data: audits.map((a) => a.earnedMediaSummary?.eeat_score ?? null), borderColor: COLORS.eeat, tension: 0.3, spanGaps: true },
      { label: 'Wikipedia', data: audits.map((a) => a.earnedMediaSummary?.wikipedia_quality_score ?? null), borderColor: COLORS.wikipedia, tension: 0.3, spanGaps: true },
      { label: 'Accuracy', data: audits.map((a) => a.earnedMediaSummary?.accuracy_score ?? null), borderColor: COLORS.accuracy, tension: 0.3, spanGaps: true },
    ],
  };

  const mentionsData = {
    labels,
    datasets: [{
      label: 'Mentions autorite',
      data: audits.map((a) => a.earnedMediaSummary?.authority_mention_count ?? 0),
      backgroundColor: COLORS.mentions,
      borderRadius: 6,
    }],
  };

  const mentionsOptions = {
    ...CHART_DEFAULTS,
    scales: {
      y: { min: 0, grid: { color: 'rgba(11,11,15,0.06)' } },
      x: { grid: { display: false } },
    },
    plugins: { ...CHART_DEFAULTS.plugins, legend: { display: false } },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <ChartCard title="Scores Earned Media dans le temps">
          <Box sx={{ height: 340 }}>
            <Line data={data} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <ChartCard title="Mentions autoritaires">
          <Box sx={{ height: 340 }}>
            <Bar data={mentionsData} options={mentionsOptions} />
          </Box>
        </ChartCard>
      </Grid>
    </Grid>
  );
}

/* ──────────────── Engines Tab ──────────────── */

function EnginesTab({ narrative, authority, referral }: {
  narrative: Audit[];
  authority: Audit[];
  referral: Audit[];
}) {
  const ENGINE_COLORS: Record<string, string> = {
    perplexity: '#20B8CD',
    openai: '#10A37F',
    chatgpt: '#10A37F',
    gemini: '#4285F4',
    claude: '#8B5CF6',
    mistral: '#FF4444',
  };

  type AxisKey = 'narrative' | 'authority' | 'referral';
  const axisColor: Record<AxisKey, string> = {
    narrative: COLORS.narrative,
    authority: COLORS.authority,
    referral: COLORS.referral,
  };

  const getScores = (a: Audit, axis: AxisKey): Record<string, number> | undefined => {
    if (axis === 'narrative') return a.brandNarrativeSummary?.engine_scores;
    if (axis === 'authority') return a.brandAuthoritySummary?.engine_scores;
    return a.brandReferralSummary?.engine_scores;
  };

  // Union of engines across all three axes.
  const allEngines = new Set<string>();
  [...narrative, ...authority, ...referral].forEach((a) => {
    (['narrative', 'authority', 'referral'] as const).forEach((axis) => {
      const scores = getScores(a, axis);
      if (scores) Object.keys(scores).forEach((e) => allEngines.add(e));
    });
  });
  const engines = Array.from(allEngines).sort();
  if (engines.length === 0) {
    return <Alert severity="info">Aucune donnée par moteur disponible.</Alert>;
  }

  // Per-axis trend line (engine × axis gets its own dataset).
  const axisAuditMap: Record<AxisKey, Audit[]> = { narrative, authority, referral };
  const axesWithData = (['narrative', 'authority', 'referral'] as const).filter((k) => axisAuditMap[k].length > 0);

  const latestByAxis: Record<AxisKey, Record<string, number> | undefined> = {
    narrative: narrative[narrative.length - 1]?.brandNarrativeSummary?.engine_scores,
    authority: authority[authority.length - 1]?.brandAuthoritySummary?.engine_scores,
    referral: referral[referral.length - 1]?.brandReferralSummary?.engine_scores,
  };

  // Heatmap-style stacked bar: for each axis, engines × last audit score.
  const latestBarData = {
    labels: axesWithData.map((k) => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: engines.map((engine) => ({
      label: engine.charAt(0).toUpperCase() + engine.slice(1),
      data: axesWithData.map((axis) => latestByAxis[axis]?.[engine] ?? 0),
      backgroundColor: ENGINE_COLORS[engine] || '#888',
      borderRadius: 4,
    })),
  };

  // Line chart: one line per (axis, engine) — up to 9 series.
  const trendLabels = (() => {
    const all = [
      ...narrative.map((a) => a.createdAt),
      ...authority.map((a) => a.createdAt),
      ...referral.map((a) => a.createdAt),
    ].sort();
    return Array.from(new Set(all)).map(formatDate);
  })();
  const trendDates = (() => {
    const all = [
      ...narrative.map((a) => a.createdAt),
      ...authority.map((a) => a.createdAt),
      ...referral.map((a) => a.createdAt),
    ].sort();
    return Array.from(new Set(all));
  })();

  const scoreAt = (dateIso: string, axis: AxisKey, engine: string): number | null => {
    const arr = axisAuditMap[axis];
    const a = arr.find((x) => x.createdAt === dateIso);
    if (!a) return null;
    return getScores(a, axis)?.[engine] ?? null;
  };

  const trendData = {
    labels: trendLabels,
    datasets: axesWithData.flatMap((axis) =>
      engines.map((engine) => ({
        label: `${axis.charAt(0).toUpperCase() + axis.slice(1)} — ${engine}`,
        data: trendDates.map((d) => scoreAt(d, axis, engine)),
        borderColor: ENGINE_COLORS[engine] || '#888',
        borderDash: axis === 'narrative' ? [] : axis === 'authority' ? [4, 4] : [2, 2],
        tension: 0.3,
        spanGaps: true,
        borderWidth: 1.5,
        pointBackgroundColor: axisColor[axis],
      })),
    ),
  };

  const stackedOptions = {
    ...CHART_DEFAULTS,
    scales: {
      y: { min: 0, max: 100, grid: { color: 'rgba(11,11,15,0.06)' } },
      x: { grid: { display: false } },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <ChartCard title="Score par moteur IA × axe (dernier audit)">
          <Box sx={{ height: 320 }}>
            <Bar data={latestBarData} options={stackedOptions} />
          </Box>
        </ChartCard>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <ChartCard title="Évolution par moteur × axe (trait plein=Narrative, tirets=Authority, pointillé=Referral)">
          <Box sx={{ height: 340 }}>
            <Line data={trendData} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
    </Grid>
  );
}

/* ──────────────── Zones Tab ──────────────── */

function ZonesTab({ audits }: { audits: Audit[] }) {
  const [selectedZone, setSelectedZone] = useState<string>('');

  const allZones = useMemo(() => {
    const zones = new Set<string>();
    audits.forEach((a) => {
      const z = a.brandReferralSummary?.zones;
      if (z) Object.keys(z).forEach((k) => zones.add(k));
    });
    return Array.from(zones).sort();
  }, [audits]);

  useEffect(() => {
    if (allZones.length > 0 && !selectedZone) setSelectedZone(allZones[0]);
  }, [allZones, selectedZone]);

  if (allZones.length === 0) {
    return <Alert severity="info">Aucune donnée de zone disponible. Configurez des localisations dans Brand Knowledge.</Alert>;
  }

  const labels = audits.map((a) => formatDate(a.createdAt));

  const zoneLineData = {
    labels,
    datasets: allZones.map((zone, i) => ({
      label: zone,
      data: audits.map((a) => a.brandReferralSummary?.zones?.[zone]?.score ?? null),
      borderColor: [COLORS.geo, COLORS.freshness, COLORS.quality, COLORS.brandPerception, COLORS.earnedMedia][i % 5],
      tension: 0.3,
      spanGaps: true,
    })),
  };

  const LEVEL_LABELS: Record<string, string> = {
    L1: 'L1 - Générique',
    L2: 'L2 - Catégorie',
    L3: 'L3 - Comparaison',
    L4: 'L4 - Pain point',
    L5: 'L5 - Marque',
  };

  const levelData = selectedZone ? {
    labels: audits.map((a) => formatDate(a.createdAt)),
    datasets: (['L1', 'L2', 'L3', 'L4', 'L5'] as const).map((level, i) => ({
      label: LEVEL_LABELS[level],
      data: audits.map((a) => a.brandReferralSummary?.zones?.[selectedZone]?.levels?.[level]?.score ?? null),
      borderColor: ['#EF4444', '#F97316', '#F59E0B', '#7C3AED', '#10B981'][i],
      tension: 0.3,
      spanGaps: true,
    })),
  } : null;

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <ChartCard title="Score par zone dans le temps">
          <Box sx={{ height: 340 }}>
            <Line data={zoneLineData} options={CHART_DEFAULTS} />
          </Box>
        </ChartCard>
      </Grid>
      {allZones.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title="Niveaux de visibilité"
            action={
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Zone</InputLabel>
                <Select value={selectedZone} onChange={(e) => setSelectedZone(e.target.value)} label="Zone">
                  {allZones.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
                </Select>
              </FormControl>
            }
          >
            {levelData && (
              <Box sx={{ height: 340 }}>
                <Line data={levelData} options={CHART_DEFAULTS} />
              </Box>
            )}
          </ChartCard>
        </Grid>
      )}
    </Grid>
  );
}

/* ──────────────── Shared components ──────────────── */

function ChartCard({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}
